import type { JumpPhysics } from './JumpPhysics';

/**
 * Can a runner get up a step that needs a double jump – and how much timing tolerance does it
 * leave? Used by the terrain analysis to prove that high steps (container roofs) are fair.
 *
 * Model (same rules as PlayerController): ground jump at t = 0 with `jumpVelocity`; the double
 * jump at t₁ sets the vertical speed to `doubleJumpVelocity`; the front of the runner reaches the
 * step face at T = D / speed (D = distance front of the runner → face at take-off). The runner gets
 * up if its feet are at most `maxStepHeight` below the top at that moment – lower, the face is a
 * wall (PlayerController.blockerIn); higher, it lands on top later.
 *
 *   height at T with a double jump at t₁:
 *     y(T; t₁) = v₀ t₁ − g t₁²/2 + v₂ (T − t₁) − g (T − t₁)²/2
 *   a concave parabola in t₁ → the trigger times that work form one interval.
 *
 * The controller integrates in fixed steps (semi-implicit Euler: speed first, then position). After
 * n steps of a launch with v the height is v·t − g·t²/2 − g·dt·t/2 – the continuous curve of a launch
 * with v − g·dt/2. `fixedStep` applies exactly that, so the windows match the game.
 */
export interface TimeWindow {
  readonly from: number;
  readonly to: number;
}

export interface StepReach {
  /** Double-jump trigger interval (s after take-off) for the best take-off point – its length is the input tolerance. */
  readonly bestTrigger: TimeWindow | null;
  /** Arrival time at the face (s after take-off) for that best take-off point. */
  readonly bestArrival: number;
  /**
   * Arrival times T (s after take-off) at which the double jump still has at least the required input
   * tolerance, or null. Take-off distances are T × speed – independent of the speed in time.
   */
  readonly arrival: TimeWindow | null;
}

/** Resolution of the arrival-time scan (s) – one 1 ms step is far below any input tolerance. */
const SCAN_STEP = 0.001;
const EPSILON = 1e-9;

/**
 * Double-jump trigger times (s after take-off) that get the runner up a step of `rise` meters above
 * the take-off ground when its front reaches the face `arrival` seconds after take-off – or null.
 * The double jump must happen in the air (before the ground jump would land) and before the face.
 */
export function doubleJumpTriggerWindow(physics: JumpPhysics, maxStepHeight: number, rise: number, arrival: number, fixedStep = 0): TimeWindow | null {
  const g = physics.gravity;
  const stepLoss = (g * fixedStep) / 2;
  const v0 = physics.jumpVelocity - stepLoss;
  const v2 = physics.doubleJumpVelocity - stepLoss;
  const needed = rise - maxStepHeight;
  // y(T; t₁) = −g t₁² + (v₀ − v₂ + gT) t₁ + v₂T − gT²/2  ≥ needed
  const b = v0 - v2 + g * arrival;
  const c = v2 * arrival - (g * arrival * arrival) / 2 - needed;
  const discriminant = b * b + 4 * g * c;
  if (discriminant < 0) return null;
  const root = Math.sqrt(discriminant);
  const groundJumpLands = (2 * v0) / g;
  const from = Math.max(EPSILON, (b - root) / (2 * g));
  const to = Math.min(arrival, groundJumpLands, (b + root) / (2 * g));
  return to > from ? { from, to } : null;
}

/**
 * Timing tolerance for a double-jump step: the best input window over all take-off points, and the
 * range of arrival times (≈ take-off points) that leave at least `minTriggerWindow` seconds for it.
 */
export function doubleJumpStepReach(physics: JumpPhysics, maxStepHeight: number, rise: number, minTriggerWindow: number, fixedStep = 0): StepReach {
  let bestTrigger: TimeWindow | null = null;
  let bestArrival = 0;
  let first: number | null = null;
  let last: number | null = null;
  for (let arrival = SCAN_STEP; arrival <= physics.maxAirTime + EPSILON; arrival += SCAN_STEP) {
    const window = doubleJumpTriggerWindow(physics, maxStepHeight, rise, arrival, fixedStep);
    const length = window ? window.to - window.from : 0;
    if (window && length > (bestTrigger ? bestTrigger.to - bestTrigger.from : 0)) {
      bestTrigger = window;
      bestArrival = arrival;
    }
    if (length >= minTriggerWindow - EPSILON) {
      first ??= arrival;
      last = arrival;
    }
  }
  return { bestTrigger, bestArrival, arrival: first !== null && last !== null ? { from: first, to: last } : null };
}

/**
 * Seconds of take-off timing that leave the required double-jump tolerance, when the runner can
 * take off at most `runUp` meters (front of the runner → face) before the step at `speed` m/s.
 */
export function takeOffWindow(reach: StepReach, runUp: number, speed: number): number {
  if (!reach.arrival || speed <= 0) return 0;
  return Math.max(0, Math.min(reach.arrival.to, runUp / speed) - reach.arrival.from);
}

// ── Targets in the air ──────────────────────────────────────────────────────

/**
 * A pickup hanging in the air (the golden bottle of SEG_GOLDEN_RAMP_60M), seen from a take-off point:
 * the runner touches it while its centre is between `from` and `to` meters ahead of the take-off point
 * (pickup volume ± half the runner's depth) and its feet are at least `rise` meters above the take-off
 * ground (pickup bottom − body height). Same rules as above: ground jump at t = 0, the double jump at t₁
 * sets the vertical speed to `doubleJumpVelocity`, fixed-step integration via `fixedStep`.
 */
export interface AirTarget {
  readonly rise: number;
  readonly from: number;
  readonly to: number;
}

/** Feet height (m above take-off) `t` seconds after a ground jump, with a double jump at `doubleAt` (s). */
export function jumpHeightAt(physics: JumpPhysics, t: number, doubleAt = Infinity, fixedStep = 0): number {
  const g = physics.gravity;
  const stepLoss = (g * fixedStep) / 2;
  const v0 = physics.jumpVelocity - stepLoss;
  if (t <= doubleAt) return v0 * t - (g * t * t) / 2;
  const s = t - doubleAt;
  return jumpHeightAt(physics, doubleAt, Infinity, fixedStep) + (physics.doubleJumpVelocity - stepLoss) * s - (g * s * s) / 2;
}

/** Highest feet height between `from` and `to` seconds after take-off (both pieces are parabolas: vertex or interval end). */
function highestBetween(physics: JumpPhysics, from: number, to: number, doubleAt: number, fixedStep: number): number {
  const g = physics.gravity;
  const stepLoss = (g * fixedStep) / 2;
  const clampTo = (t: number, a: number, b: number) => Math.min(b, Math.max(a, t));
  let best = -Infinity;
  if (from < doubleAt) {
    const end = Math.min(to, doubleAt);
    best = jumpHeightAt(physics, clampTo((physics.jumpVelocity - stepLoss) / g, from, end), doubleAt, fixedStep);
  }
  if (to > doubleAt) {
    const start = Math.max(from, doubleAt);
    const vertex = doubleAt + (physics.doubleJumpVelocity - stepLoss) / g;
    best = Math.max(best, jumpHeightAt(physics, clampTo(vertex, start, to), doubleAt, fixedStep));
  }
  return best;
}

/** Overlap time span (s after take-off) of an air target at `speed`, or null if it lies behind. */
function overlapTimes(target: AirTarget, speed: number): TimeWindow | null {
  if (speed <= 0 || target.to <= 0) return null;
  return { from: Math.max(0, target.from) / speed, to: target.to / speed };
}

/** A single jump (no double jump) from the take-off point touches the target. */
export function singleJumpReaches(physics: JumpPhysics, target: AirTarget, speed: number, fixedStep = 0): boolean {
  const span = overlapTimes(target, speed);
  return span !== null && highestBetween(physics, span.from, span.to, Infinity, fixedStep) >= target.rise - EPSILON;
}

/**
 * Double-jump trigger times (s after take-off) that touch the target – the longest run of them, or null.
 * The double jump must happen in the air, before the ground jump would land on the take-off height.
 */
export function airTargetTriggerWindow(physics: JumpPhysics, target: AirTarget, speed: number, fixedStep = 0): TimeWindow | null {
  const span = overlapTimes(target, speed);
  if (!span) return null;
  const groundJumpLands = (2 * (physics.jumpVelocity - (physics.gravity * fixedStep) / 2)) / physics.gravity;
  let best: TimeWindow | null = null;
  let runFrom: number | null = null;
  let previous = 0;
  for (let t1 = SCAN_STEP; t1 < groundJumpLands; t1 += SCAN_STEP) {
    const hits = highestBetween(physics, span.from, span.to, t1, fixedStep) >= target.rise - EPSILON;
    if (hits) runFrom ??= t1;
    if ((!hits || t1 + SCAN_STEP >= groundJumpLands) && runFrom !== null) {
      const end = hits ? t1 : previous;
      if (!best || end - runFrom > best.to - best.from) best = { from: runFrom, to: end };
      runFrom = null;
    }
    previous = t1;
  }
  return best;
}

/** Highest the feet get above the take-off ground: ground jump plus a double jump at the apex. */
export function highestDoubleJump(physics: JumpPhysics, fixedStep = 0): number {
  const apex = (physics.jumpVelocity - (physics.gravity * fixedStep) / 2) / physics.gravity;
  return highestBetween(physics, apex, Infinity, apex, fixedStep);
}
